import React, { type ReactNode, useRef, useEffect } from 'react';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
	fireScreenAnalytics,
	fireTrackAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useReportCoreData } from '../../services/deployment-frequency-core/index.tsx';
import { isDataAbsolutelyEmpty } from '../../services/deployment-frequency-core/utils.tsx';
import { useProjectAnalyticsAttributes } from '../../services/get-project/index.tsx';

type Props = {
	children: ReactNode;
};

// This component is not part of DeploymentFrequencyDataAnalyticsWrapper since it needs access to the ContextualAnalyticsData
const DeploymentFrequencyDataAnalytics = () => {
	const { data, loading } = useReportCoreData();

	const hasFiredRef = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}

		if (!loading && data) {
			const attributes: Attributes = {
				hasSufficientData: !isDataAbsolutelyEmpty(data),
				hasDeploymentProvider: data.hasDeploymentProvider,
			};
			fireScreenAnalytics(createAnalyticsEvent({}), attributes);
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'report viewed',
				'deploymentFrequency',
				attributes,
			);
			hasFiredRef.current = true;
		}
	}, [createAnalyticsEvent, data, loading]);

	return null;
};

const DeploymentFrequencyDataAnalyticsWrapper = ({ children }: Props) => {
	const projectInfo = useProjectAnalyticsAttributes();

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="deploymentFrequencyReportPage"
			attributes={{ ...projectInfo }}
		>
			<DeploymentFrequencyDataAnalytics />
			{children}
		</ContextualAnalyticsData>
	);
};

export default DeploymentFrequencyDataAnalyticsWrapper;
